import { Button as KitButton, buttonClassName, type ButtonVariant as KitVariant } from '@easymusic/ui';
import type { ButtonHTMLAttributes } from 'react';

/**
 * The application's button, drawn by the UI kit.
 *
 * The names are the application's own (what the action is to the page), the look is
 * the kit's: primary: the main action; secondary: any other action; ghost: an action
 * inside a line or a list; sound: playing and recording.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'sound';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const VARIANTS: Record<Variant, KitVariant> = {
  primary: 'primary',
  secondary: 'ghost',
  ghost: 'text',
  sound: 'secondary',
};

export function Button({ variant = 'primary', size = 'md', children, ...props }: ButtonProps) {
  return (
    // The kit sizes a button by the device; `lg` is its usual size.
    <KitButton variant={VARIANTS[variant]} size={size === 'sm' ? 'sm' : 'md'} {...props}>
      {children}
    </KitButton>
  );
}

/**
 * The classes of a link that reads as a button: an action that is a navigation
 * ("Take the test", "New lesson"). It stays a link, so it opens in a new tab, and
 * looks exactly like the button of the same variant beside it.
 */
export const linkButton = (variant: 'primary' | 'secondary' = 'primary', size: Size = 'md') =>
  buttonClassName({ variant: VARIANTS[variant], size: size === 'sm' ? 'sm' : 'md' });
